import React from 'react';

type UseScaleOnResizeArgs = {
  baseWidth: number;
  baseHeight: number;
};

export function useScaleOnResize<TElement extends HTMLElement>(
  args: UseScaleOnResizeArgs,
): {
  containerRef: React.RefObject<TElement>;
  containerStyle: React.CSSProperties;
  subContainerStyle: React.CSSProperties;
} {
  const { baseWidth, baseHeight } = args;
  const containerRef = React.useRef<TElement | null>(null);
  const [scale, setScale] = React.useState(1);

  // Scale card to fit container width
  React.useLayoutEffect((): void | (() => void) => {
    const el = containerRef.current;
    if (!el) return undefined;

    const update = (w: number): void => {
      setScale(Math.min(1, w / baseWidth));
    };

    update(el.clientWidth || baseWidth);

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (entry?.contentRect.width) {
        update(entry.contentRect.width);
      }
    });

    resizeObserver.observe(el);

    return (): void => {
      resizeObserver.disconnect();
    };
  }, []);

  return {
    containerRef,
    containerStyle: {
      // Reserve layout height so nothing “squeezes” as we scale
      width: '100%',
      maxWidth: baseWidth,
      height: baseHeight * scale,
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
    },
    subContainerStyle: {
      width: baseWidth,
      height: baseHeight,
    },
  };
}
